import { Controller } from 'stimulus'
import { createConsumer } from "@rails/actioncable";
import Sortable from 'sortablejs';

export default class extends Controller {
  static targets = ['kanbanElement']

  connect() {
    var kanbanLists = this.element.querySelectorAll(".kanban-list-items");

    kanbanLists.forEach((list, index) => {
      new Sortable(list, {
        group: "kanban",
        animation: 150,
        ghostClass: "sortable-ghost",
        dragClass: "sortable-drag",
        scroll: true,
        scrollSensitivity: 100,
        scrollSpeed: 10,
        emptyInsertThreshold: 150,
        forceFallback: true,
        fallbackTolerance: 15,
        swapThreshold: 0.7,
        invertSwap: false,

        pull: true,
        put: true,
        bubbleScroll: true,

        onStart: (evt) => {
          this.currentDragTarget = null;

          document.querySelectorAll('.kanban-list.collapsed .kanban-list-items').forEach(collapsedList => {
            console.log('  - Enabling collapsed list:', collapsedList);
            collapsedList.classList.add('drop-enabled');
            const header = collapsedList.closest('.kanban-list').querySelector('.list-title-wrapper');
            if (header) {
              header.classList.add('drop-active');
            }
          });
        },

        onMove: (evt) => {
          const targetList = evt.to.closest('.kanban-list');
          if (!targetList) return true;

          if (this.currentDragTarget && this.currentDragTarget !== targetList) {
            document.querySelectorAll('.kanban-list-items').forEach(list => {
              list.classList.remove('sortable-hover');
            });
          }

          const isCollapsed = targetList.classList.contains('collapsed');

          if (isCollapsed) {
            const rect = targetList.getBoundingClientRect();
            const pointerX = evt.originalEvent?.clientX || evt.clientX;

            const hitZoneLeft = rect.left + rect.width * 0.3;
            const hitZoneRight = rect.right - rect.width * 0.3;

            if (pointerX > hitZoneLeft && pointerX < hitZoneRight) {
              evt.to.classList.add('sortable-hover');
              this.currentDragTarget = targetList;

              const header = targetList.querySelector('.list-title-wrapper');
              if (header) {
                header.classList.add('drop-success');
              }
              return true;
            }

            const header = targetList.querySelector('.list-title-wrapper');
            if (header) header.classList.remove('drop-success');

            return false;
          }

          const rect = targetList.getBoundingClientRect();
          const pointerX = evt.originalEvent?.clientX || evt.clientX;
          const margin = 12;
          const insideCollapsed = pointerX > rect.left + margin && pointerX < rect.right - margin;

          if (insideCollapsed) {
            evt.to.classList.add('sortable-hover');
            this.currentDragTarget = targetList;
            return true;
          }

          return false;
        },

        onEnd: (evt) => {
          this.currentDragTarget = null;

          document.querySelectorAll('.kanban-list-items').forEach(list => {
            list.classList.remove('drop-enabled', 'sortable-hover');
          });
          document.querySelectorAll('.list-title-wrapper').forEach(header => {
            header.classList.remove('drop-active', 'drop-success');
          });

          const targetList = evt.to.closest('.kanban-list');
          if (targetList?.classList.contains('collapsed')) {
            const header = targetList.querySelector('.list-title-wrapper');
            if (header) {
              header.classList.add('drop-success');
              setTimeout(() => header.classList.remove('drop-success'), 800);
            }
          }

          this.changePosition(evt.item, evt.to, evt.from, evt.oldIndex, evt.newIndex);
        }
      });
    });

    const boardId = this.element.dataset.kanbanBoardId || 
                    this.element.closest('[data-kanban-board-id]')?.dataset.kanbanBoardId;
    const accountId = this.element.dataset.kanbanAccountId || 
                      this.element.closest('[data-kanban-account-id]')?.dataset.kanbanAccountId;

    if (boardId && accountId) {
      this.channel = createConsumer().subscriptions.create(
        { 
          channel: "KanbanChannel", 
          board_id: boardId,
          account_id: accountId
        },
        {
          connected() {
            console.log("Connected to KanbanChannel for board", boardId);
          },
          disconnected() {
            console.log("Disconnected from KanbanChannel");
          },
          received: this.handleKanbanBroadcast.bind(this)
        }
      );
    } else {
      console.warn("KanbanController: board_id or account_id not found, cannot subscribe to channel");
    }
  }

  handleKanbanBroadcast(data) {
    if (data.type === 'task_completion') {
      this.updateNextTaskOnCard(data);
      return;
    }

    const { task_data } = data;

    try {
      if (task_data.update_type === 'update_kanban_position') {
        const el = this.element.querySelector(`#project-${task_data.project_id}`);
        const source = this.element.querySelector(`#kanban-list-items-${task_data.source_kanban_list_id}`);
        const target = this.element.querySelector(`#kanban-list-items-${task_data.kanban_list_id}`);

        if (!el || !source || !target) {
          console.warn("Element(s) not found for update_kanban_position", { el, source, target });
          return;
        }

        const position = task_data.position - 1;
        const targetItems = Array.from(target.children).filter(item => item.classList.contains('index-item'));

        if (position < targetItems.length) {
          target.insertBefore(el, targetItems[position]);
        } else {
          target.appendChild(el);
        }

        this.updateOrderedProjects(task_data.ordered_projects, target);
        this.updateTaskUI(task_data, el, source, target);
        this.updateListCounts(task_data.project_counts, task_data.list_statuses);
      }
      else if (task_data.update_type === 'project_create') {
        const target = this.element.querySelector(`#kanban-list-items-${task_data.kanban_list_id}`);
        if (task_data.kanban_html && target) {
          target.insertAdjacentHTML('afterbegin', task_data.kanban_html);
          this.updateListCounts(task_data.project_counts, task_data.list_statuses);
        }
      }
      else if (['archive_project', 'destroy_project'].includes(task_data.update_type)) {
        const el = this.element.querySelector(`#project-${task_data.project_id}`);
        if (el) {
          el.remove();
          this.updateListCounts(task_data.project_counts, task_data.list_statuses);
        }
      }
      else if (task_data.update_type === 'update_project') {
        const breadcrumb = document.querySelector(".breadcrumbs a:nth-of-type(2)");
        if (breadcrumb) breadcrumb.innerHTML = task_data.updated_title;
        
        
        const projectTitleElements = document.querySelectorAll(`.project_title${task_data.project_id}`);
        projectTitleElements.forEach((el) => {
          el.innerHTML = task_data.updated_title;
        });
      }
      else if (task_data.update_type === 'toggle_collapse') {
        const { kanban_list_id, collapsed } = task_data;
        const listEl = document.querySelector(`#kanban-list-${kanban_list_id}`);
        if (!listEl) return;

        const collapseControllerElement = listEl.querySelector('.list-title-wrapper[data-controller~="kanban-collapse"]');
        if (collapseControllerElement && collapseControllerElement.controllers) {
          const collapseController = collapseControllerElement.controllers.find(
            c => c.identifier === 'kanban-collapse'
          );
          if (collapseController && typeof collapseController.setCollapseState === 'function') {
            collapseController.setCollapseState(kanban_list_id, collapsed);
            return;
          }
        }

        const content      = listEl.querySelector('.kanbanlist');
        const header       = listEl.querySelector('.kanban-list-header');
        const settingsWrap = listEl.querySelector('.kanban-list-settings-wrapper');
        const toggleButton = listEl.querySelector('.collapse-toggle');
        const icon         = toggleButton && toggleButton.querySelector('.icon');

        listEl.classList.toggle('collapsed', collapsed);
        if (content)      content.classList.toggle('hidden', collapsed);
        if (header)       header.classList.toggle('is-collapsed', collapsed);
        if (settingsWrap) settingsWrap.classList.toggle('hidden', collapsed);

        if (toggleButton && icon) {
          if (collapsed) {
            icon.classList.remove('icon-arrow-down2');
            icon.classList.add('icon-arrow-right2');
            toggleButton.title = 'Expand column';
          } else {
            icon.classList.remove('icon-arrow-right2');
            icon.classList.add('icon-arrow-down2');
            toggleButton.title = 'Collapse column';
          }
        }
      }
      else if (task_data.update_type === 'update_limits') {
        const { kanban_list_id, header_html } = task_data;
        if (!header_html) return;

        const kanbanList = document.querySelector(`#kanban-list-${kanban_list_id}`);
        if (!kanbanList) {
          console.warn(`No kanban list element for #kanban-list-${kanban_list_id}`);
          return;
        }

        const header = kanbanList.querySelector('.kanban-list-header');
        if (!header) {
          console.warn(`No .kanban-list-header inside #kanban-list-${kanban_list_id}`);
          return;
        }

        header.innerHTML = header_html;
      }
      else if (task_data.update_type === 'update_kanban_title') {
        const { kanban_list_id, header_html } = task_data;
        if (!header_html) return;

        const kanbanList = document.querySelector(`#kanban-list-${kanban_list_id}`);
        if (!kanbanList) {
          console.warn(`No kanban list element for #kanban-list-${kanban_list_id}`);
          return;
        }

        const header = kanbanList.querySelector('.kanban-list-header');
        if (header) {
          header.innerHTML = header_html;
          header.classList.remove('editing');
        }
      }
      else if (task_data.update_type === 'create_delete_kanban_list') {
        const { kanban_list_html } = task_data;

        if (!kanban_list_html) return;

        const kanban_board_wrap = document.querySelector("#projects-kanban");
        if (kanban_board_wrap) {
          kanban_board_wrap.innerHTML = kanban_list_html;
          kanban_board_wrap.querySelector(".kanban-lists").scrollLeft = 999999;
        }
      }
    } catch (error) {
      console.error("Error handling Kanban broadcast:", error, task_data);
    }
  }

  updateTaskUI(data, el, source, target) {
    const sourceKanbanId = data.source_kanban_list_id;
    this.changeDeleteOption(data, sourceKanbanId);
    this.changeDropdownLinks(data, el, target);
    if (source !== target) {
      this.changeDropdownLinks(data, el, source);
    }
    this.updateKanbanInOptions(data, el);
  }

  updateOrderedProjects(orderedProjects, target) {
    orderedProjects.forEach((projectId) => {
      const projectEl = this.element.querySelector(`#project-${projectId}`);
      if (projectEl) {
        target.appendChild(projectEl);
      }
    });
  }

  changePosition(el, target, source, oldIndex, newIndex) {
    var elementId = this.getElementId(el.id);
    var targetKanbanId = this.getElementId(target.parentNode.id);
    var sourceKanbanId = this.getElementId(source.parentNode.id);

    let sibling = el.nextElementSibling;
    let siblingId = sibling ? this.getElementId(sibling.id) : null;

    let data = { 
      kanban_list_id: targetKanbanId, 
      source_kanban_list_id: sourceKanbanId, 
      id: elementId, 
      sibling_id: siblingId 
    };

    fetch(`/${accountId}/projects/${elementId}/update_kanban_position`, {
      method: "PATCH",
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json'
      }
    }).then((response) => {
      if (!response.ok) {
        throw response
      }
      return response.json();
    }).then((responseJson) => {
      this.changeDeleteOption(responseJson, sourceKanbanId);
      this.changeDropdownLinks(responseJson, el, target);
      if (source !== target) {
        this.changeDropdownLinks(responseJson, el, source);
      }
      this.updateKanbanInOptions(responseJson, el)

      this.updateListCounts(responseJson.project_counts, responseJson.list_statuses)
    }).catch((error) => {
      console.log(error)
    })
  }

  getElementId(elementId) {
    var lastHyphenIndex = elementId.lastIndexOf('-');
    return elementId.substring(lastHyphenIndex + 1);
  }

  changeDeleteOption(responseJson, sourceKanbanId) {
    var kanban_board = document.querySelector(`#kanban-list-${responseJson.kanban_list_id}`);
    var dropdown_delete_wrap = this.getDeleteWrap(kanban_board);
    dropdown_delete_wrap.insertAdjacentHTML("beforeEnd", `<a data-remote="true" data-method="get" 
          href="/${responseJson.account_id}/boards/${responseJson.board_id}/kanban_lists/${responseJson.kanban_list_id}/delete_with_project">
          <span class="icon icon-bin2 icon-left"></span> Delete List</a>`)

    var source_kanban_board = document.querySelector(`#kanban-list-${sourceKanbanId}`);
    var source_kanban_board_item = source_kanban_board.querySelector(`#kanban-list-items-${sourceKanbanId}`);
    if (source_kanban_board_item.querySelector("a.item-link") === null) {
      var source_dropdown_delete_wrap = this.getDeleteWrap(source_kanban_board);
      source_dropdown_delete_wrap.insertAdjacentHTML("beforeEnd", `<a data-target="delete.link"
            data-action="ajax:beforeSend->delete#click" data-remote="true" rel="nofollow"
             data-method="delete" href="/${responseJson.account_id}/boards/${responseJson.board_id}/kanban_lists/${sourceKanbanId}">
            <span class="icon icon-bin2 icon-left"></span> Delete List</a>`)
    }
  }

  getDeleteWrap(kanban_board) {
    var kanban_setting = kanban_board.querySelector("#kanban-list-settings");
    var dropdown_delete_wrap = kanban_setting.querySelector(".delete-wrap");
    var delete_element = dropdown_delete_wrap.querySelector("a");
    if (delete_element) {
      delete_element.parentNode.removeChild(delete_element);
    }
    return dropdown_delete_wrap
  }

  changeDropdownLinks(responseJson, element, target) {
    let children = target.children;
    for (var i = 0; i < children.length; i++) {
      var childElement = children[i];
      var projectId = this.getElementId(childElement.id);

      var dropContent = childElement.querySelector(".item-settings .dropdown-content");
      var sendToTopLink = dropContent.querySelector(".send_to_top");
      var sendToBottomLink = dropContent.querySelector(".send_to_bottom");
      if (sendToTopLink !== null && sendToTopLink !== undefined) {
        dropContent.removeChild(dropContent.querySelector(".send_to_top"));
      }
      if (sendToBottomLink !== null && sendToBottomLink !== undefined) {
        dropContent.removeChild(dropContent.querySelector(".send_to_bottom"));
      }

      if (children.length > 1) {
        if (i !== 0)
          this.addSendToTop(responseJson, projectId, childElement);

        if (i !== (children.length - 1))
          this.addSendToBottom(responseJson, projectId, childElement);
      }
    }
  }

  addSendToTop(responseJson, projectId, element) {
    let dropdown = element.querySelector(".item-settings .dropdown-content");
    dropdown.insertAdjacentHTML("afterbegin", `<a class="send_to_top" 
        data-action="click->show-hide#toggle click->dropdown#toggle click->kanban#moveToTop" href="javascript:void(0)">
        <span class="icon icon-arrow-up16 icon-left"></span> Send to top
    </a>`);
  }

  addSendToBottom(responseJson, projectId, element) {
    let top_option = element.querySelector(".item-settings .dropdown-content .send_to_top");
    if (top_option) {
      top_option.insertAdjacentHTML("afterend", `<a class="send_to_bottom" 
        data-action="click->show-hide#toggle click->dropdown#toggle click->kanban#moveToBottom" href="javascript:void(0)">
        <span class="icon icon-arrow-down16 icon-left"></span> Send to bottom
      </a>`);
    } else {
      let dropdown = element.querySelector(".item-settings .dropdown-content");
      dropdown.insertAdjacentHTML("afterbegin", `<a class="send_to_bottom" 
        data-action="click->show-hide#toggle click->dropdown#toggle click->kanban#moveToBottom" href="javascript:void(0)">
        <span class="icon icon-arrow-down16 icon-left"></span> Send to bottom
      </a>`);
    }
  }

  updateKanbanInOptions(responseJson, el) {
    el.querySelector("#kanban_list_id").innerHTML = '';
    let optionKey = Object.keys(responseJson.kanban_list);
    var optionList = '';
    for (var i = 0; i < optionKey.length; i++) {
      optionList += `<option value="${optionKey[i]}">${responseJson.kanban_list[optionKey[i]]}</option>`;
    }

    el.querySelector("#kanban_list_id").innerHTML = optionList;
  }

  updateListCounts(projectCounts, listStatuses = {}) {
    console.log('updateListCounts called with:', {
      projectCounts,
      listStatuses
    });

    if (!projectCounts) {
      console.warn('updateListCounts: projectCounts is null/undefined');
      return;
    }

    Object.keys(projectCounts).forEach((listId) => {
      const count  = projectCounts[listId];
      const status = listStatuses[listId];

      const listElement = document.querySelector(`#kanban-list-${listId}`);
      if (!listElement) {
        console.warn(`No DOM element found for #kanban-list-${listId}`);
        return;
      }

      const countWrapper = listElement.querySelector('.kanban-project-count');
      const countSpan    = countWrapper && countWrapper.querySelector('.projects_count');
      if (countSpan) countSpan.textContent = count;

      if (countWrapper) {
        countWrapper.classList.remove('below-min', 'above-max', 'within-limits');
        if (status) {
          const cls = status.replace('_', '-');
          countWrapper.classList.add(cls);
        }
      }

      const listWrapper = listElement.querySelector('.kanbanlist');
      if (listWrapper) listWrapper.dataset.projectsCount = count;

      const indicator = listElement.querySelector('.limit-indicator');
      if (indicator) {
        indicator.classList.remove('below-min', 'above-max', 'within-limits');

        if (status) {
          const cls = status.replace('_', '-');
          indicator.classList.add(cls);

          const iconHtml =
            status === 'below_min'  ? '<i class="fa fa-arrow-down"></i>' :
            status === 'above_max'  ? '<i class="fa fa-arrow-up"></i>' :
                                      '<i class="fa fa-check"></i>';

          indicator.innerHTML = iconHtml;

          let title;
          if (status === 'below_min') {
            title = "Below minimum";
          } else if (status === 'above_max') {
            title = "Above maximum";
          } else {
            title = "Within limits";
          }

          indicator.setAttribute('title', title);
        }
      }

      const titleEl = listElement.querySelector('.list-title');
      if (titleEl) {
        titleEl.classList.remove('limit-warning');

        if (status === 'below_min' || status === 'above_max') {
          titleEl.classList.add('limit-warning');
        }
      }
    });
  }


  dragAndDropProject(event, option) {
    let moveDiv = event.target;
    while (!moveDiv.classList.contains("index-item")) {
      moveDiv = moveDiv.parentElement;
    }

    var projectElement = moveDiv;
    var targetKanban = moveDiv;
    const sourceKanban = projectElement.closest(".kanban-list-items");

    if (option === "changeList") {
      let targetList = moveDiv.querySelector("#kanban_list_id").value;
      targetKanban = document.querySelector(`#kanban-list-items-${targetList}`);
    } else {
      while (!targetKanban.classList.contains("kanban-list-items")) {
        targetKanban = moveDiv.parentElement;
      }
    }

    if (option === "moveToBottom" || option === "changeList")
      targetKanban.appendChild(projectElement);
    else
      targetKanban.insertAdjacentElement("afterbegin", projectElement);

    if (option === "changeList") {
      this.changePosition(projectElement, targetKanban, sourceKanban, null, null)
    } else {
      this.changePosition(
        projectElement,
        targetKanban,
        projectElement.closest(".kanban-list-items"),
        null,
        null
      );
    }
  }

  changeList(event) {
    this.dragAndDropProject(event, "changeList");
  }

  moveToBottom(event) {
    this.dragAndDropProject(event, "moveToBottom");
  }

  moveToTop(event) {
    this.dragAndDropProject(event, "moveToTop");
  }

  updateNextTaskOnCard(data) {
    const { project_id, next_task_html, next_task_id } = data;
    
    const projectCard = this.element.querySelector(`#project-${project_id}`);
    if (!projectCard) {
      console.warn(`Project card #project-${project_id} not found`);
      return;
    }

    const itemMeta = projectCard.querySelector('.item-meta');
    if (!itemMeta) {
      console.warn(`Item meta not found for project #project-${project_id}`);
      return;
    }

    const existingNextTaskCard = itemMeta.querySelector('.next-task-card');
    
    if (next_task_html && next_task_html.trim() !== '') {
      if (existingNextTaskCard) {
        existingNextTaskCard.outerHTML = next_task_html;
      } else {
        const projectCardFields = itemMeta.querySelector('.board-card-fields');
        const dateRangeCard = itemMeta.querySelector('.project-dates-card');
        
        if (projectCardFields) {
          projectCardFields.insertAdjacentHTML('afterend', next_task_html);
        } else if (dateRangeCard) {
          dateRangeCard.insertAdjacentHTML('beforebegin', next_task_html);
        } else {
          itemMeta.insertAdjacentHTML('afterbegin', next_task_html);
        }
      }
    } else {
      if (existingNextTaskCard) {
        existingNextTaskCard.remove();
      }
    }

    if (window.Stimulus) {
      const updatedCard = projectCard.querySelector('.next-task-card');
      if (updatedCard) {
        window.Stimulus.load(updatedCard);
      }
    }
  }
}
